import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from "@angular/cdk/drag-drop";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import * as _ from "lodash";
import { NgxSpinnerService } from "ngx-spinner";
import { Subscription } from "rxjs";
import { ServiceRequestService } from "src/app/modules/service-request/service-request.service";
import { Alert } from "src/app/shared/class/Alert";
import { globalConst } from "src/app/shared/helpers/constants";
import { Regional } from "src/app/shared/models/regional";
import { ServiceLocal } from "src/app/shared/models/service-request.model";
import { User } from "src/app/shared/models/user.model";
import { UserService } from "../../user.service";
import { Company } from "./../../../../models/company";

@Component({
  selector: "app-user-details",
  templateUrl: "./user-details.component.html",
  styleUrls: ["./user-details.component.scss"],
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  goback = "../list/";
  subscription = new Subscription();
  itemsPerPage = 5;
  currentPage = 1;
  queryParams: any = {};
  userLoaded: User = {};
  hidePassword = true;
  hideConfirmPassword = true;
  userForm: FormGroup;
  optionsUserType = globalConst.options.userTypes;

  companiesAvailable: Company[] = [];
  companiesAssociate: Company[] = [];
  regionalsAvailable: Regional[] = [];
  regionalsAssociate: Regional[] = [];
  serviceLocalsAvailable: ServiceLocal[] = [];
  serviceLocalsAssociate: ServiceLocal[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private spinner: NgxSpinnerService,
    private fb: FormBuilder,
    private serviceRequestService: ServiceRequestService
  ) {}

  async ngOnInit() {
    try {
      this.spinner.show();
      this.userForm = this.fb.group(
        {
          type: this.fb.control(null, [Validators.required]),
          name: this.fb.control("", [Validators.required]),
          username: this.fb.control("", [Validators.required]),
          email: this.fb.control("", [Validators.required, Validators.email]),
          phone: this.fb.control(""),
          function: this.fb.control(""),
          active: this.fb.control(true, [Validators.required]),
          password: this.fb.control("", [
            Validators.required,
            Validators.minLength(6),
          ]),
          confirmPassword: this.fb.control("", [
            Validators.required,
            Validators.minLength(6),
          ]),
        },
        { validator: this.checkPassword }
      );

      this.route.queryParams.subscribe(
        (params: any) => (this.queryParams = params)
      );

      await this.load();
      await this.loadCompanies();
      await this.loadRegionals();
      await this.loadServiceLocals();
      await this.treatAssociations();

      this.userForm.controls.type.valueChanges.subscribe(() => {
        if (!this.userForm.controls.type.errors) {
          _.map(this.companiesAssociate, (company) => {
            this.companiesAvailable.push(company);
          });
          this.companiesAssociate = [];
        }
      });
      this.spinner.hide();
    } catch (error) {
      console.error(error);
      this.spinner.hide();
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async dropCompany(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      this.spinner.show();
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      await this.reloadCompanyOptions();
      await this.reloadRegionalOptions();
      this.spinner.hide();
    }
  }

  private async reloadCompanyOptions() {
    const newRegionalsAvailable: Regional[] = [];
    const newRegionalsAssociate: Regional[] = [];
    await this.loadRegionals();

    this.companiesAssociate.map((company) => {
      this.companiesAvailable = this.companiesAvailable.filter(
        (obj) => obj.id !== company.id
      );
      this.regionalsAvailable
        .filter(
          (obj) =>
            obj.companyAlternativeIdentifier === company.alternativeIdentifier
        )
        .map((regional) => {
          newRegionalsAvailable.push(regional);
        });
      this.regionalsAssociate
        .filter(
          (obj) =>
            obj.companyAlternativeIdentifier === company.alternativeIdentifier
        )
        .map((regional) => {
          newRegionalsAssociate.push(regional);
        });
      newRegionalsAvailable.sort((a, b) =>
        a.description > b.description ? 1 : -1
      );
      newRegionalsAssociate.sort((a, b) =>
        a.description > b.description ? 1 : -1
      );
    });
    this.regionalsAssociate = newRegionalsAssociate;
    const regionalsToShow: Regional[] = [];
    newRegionalsAvailable.map((newRegional) => {
      const found = this.regionalsAssociate.filter(
        (obj) => obj.id === newRegional.id
      );
      if (_.isEmpty(found)) {
        regionalsToShow.push(newRegional);
      }
    });
    this.regionalsAvailable = regionalsToShow;
  }

  async dropRegional(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      this.spinner.show();
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      await this.reloadRegionalOptions();
      this.spinner.hide();
    }
  }

  private async reloadRegionalOptions() {
    await this.loadServiceLocals();
    const newServiceLocalsAvailable: ServiceLocal[] = [];
    const newServiceLocalsAssociate: ServiceLocal[] = [];
    this.regionalsAssociate.map((regionalAssociate) => {
      const foundAvailable = this.serviceLocalsAvailable.filter(
        (obj) => obj.regionalId === regionalAssociate.id
      );
      foundAvailable.map((found) => newServiceLocalsAvailable.push(found));
      this.serviceLocalsAssociate
        .filter((obj) => obj.regionalId === regionalAssociate.id)
        .map((serviceLocal) => {
          newServiceLocalsAssociate.push(serviceLocal);
        });
      newServiceLocalsAvailable.sort((a, b) =>
        a.description > b.description ? 1 : -1
      );
      newServiceLocalsAssociate.sort((a, b) =>
        a.description > b.description ? 1 : -1
      );
    });
    this.serviceLocalsAssociate = newServiceLocalsAssociate;
    this.serviceLocalsAvailable = newServiceLocalsAvailable;
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  async treatAssociations() {
    if (this.queryParams.id) {
      this.companiesAssociate = this.userLoaded.companiesAssociate;
      await this.reloadCompanyOptions();
      this.regionalsAssociate = this.userLoaded.regionalsAssociate;
      this.regionalsAssociate.map(async (regional) => {
        this.regionalsAvailable = this.regionalsAvailable.filter(
          (obj) => obj.id !== regional.id
        );
      });
      this.serviceLocalsAssociate = this.userLoaded.serviceLocalsAssociate;
      this.serviceLocalsAssociate.map(async (serviceLocal) => {
        this.serviceLocalsAvailable = this.serviceLocalsAvailable.filter(
          (obj) => obj.id !== serviceLocal.id
        );
      });
    }
  }

  async filterItems() {
    if (this.companiesAssociate.length > 0) {
      this.spinner.show();
      this.regionalsAvailable = [];
      await this.loadRegionals();
      this.companiesAssociate.map((company) => {
        this.regionalsAvailable.filter(
          (obj) =>
            obj.companyAlternativeIdentifier === company.alternativeIdentifier
        );
      });
      this.spinner.hide();
    }
  }

  async loadCompanies() {
    const data: any = await this.serviceRequestService.getCompanies();
    this.companiesAvailable = data.status === undefined ? data : [];
    this.companiesAvailable.sort((a, b) =>
      a.description > b.description ? 1 : -1
    );
  }

  async loadRegionals() {
    const data: any = await this.serviceRequestService.getRegionals();
    this.regionalsAvailable = data.status === undefined ? data : [];
    this.regionalsAvailable.sort((a, b) =>
      a.description > b.description ? 1 : -1
    );
  }

  async loadServiceLocals() {
    const data: any = await this.serviceRequestService.getServiceLocals();
    this.serviceLocalsAvailable = data.status === undefined ? data : [];
    this.serviceLocalsAvailable.sort((a, b) =>
      a.description > b.description ? 1 : -1
    );
  }

  async load() {
    if (this.queryParams.id) {
      this.spinner.show();
      try {
        this.userForm.get("password").clearValidators();
        this.userForm.get("confirmPassword").clearValidators();
        const data: any = await this.userService.searchUser(
          this.queryParams.id
        );
        this.userLoaded = data.status === undefined ? data : {};
        this.userForm.setValue({
          ...this.userForm.value,
          type: this.userLoaded.type,
          name: this.userLoaded.name,
          username: this.userLoaded.username,
          email: this.userLoaded.email,
          active: this.userLoaded.active,
        });
      } catch (error) {
        this.spinner.hide();
        Alert.error(Alert.getMessage().errors.load).then(() =>
          this.router.navigate(["/user/list"])
        );
      }
    }
  }

  loadFormData() {
    this.spinner.show();
    this.userForm.setValue({
      ...this.userForm.value,
    });

    this.spinner.hide();
  }

  onSubmit() {
    if (this.userForm.valid) {
      if (!this.queryParams.id) {
        this.save();
      } else {
        this.update();
      }
    } else {
      this.userForm.markAllAsTouched();
    }
  }

  async save() {
    this.spinner.show();
    const data = {
      ...this.userForm.value,
      companiesAssociate: this.companiesAssociate,
      regionalsAssociate: this.regionalsAssociate,
      serviceLocalsAssociate: this.serviceLocalsAssociate,
    };
    delete data.confirmPassword;

    const userExist = await this.checkUserId();

    if (parseInt(userExist, 10) === 0) {
      this.subscription.add(
        this.userService.registerUser({ ...data }).subscribe(
          () => {
            this.spinner.hide();
            Alert.success(Alert.getMessage().success.save).then(() =>
              this.router.navigate(["/user/list"])
            );
          },
          () => {
            this.spinner.hide();
            Alert.error(Alert.getMessage().errors.save);
          }
        )
      );
    } else {
      this.spinner.hide();
      Alert.error(Alert.getMessage().errors.user.userExist).then(() => {
        document.getElementById("username").focus();
      });
    }
  }

  async update() {
    Alert.question(Alert.getMessage().question.update).then(
      async (response) => {
        if (response.value) {
          this.spinner.show();
          const data = {
            ...this.userForm.value,
            id: this.queryParams.id,
            companiesAssociate: this.companiesAssociate,
            regionalsAssociate: this.regionalsAssociate,
            serviceLocalsAssociate: this.serviceLocalsAssociate,
          };

          delete data.confirmPassword;

          this.subscription.add(
            this.userService.updateUser({ ...data }).subscribe(
              () => {
                this.spinner.hide();
                Alert.success(Alert.getMessage().success.update).then(() =>
                  this.router.navigate(["/user/list"])
                );
              },
              () => {
                this.spinner.hide();
                Alert.error(Alert.getMessage().errors.update);
              }
            )
          );
        }
      }
    );
  }

  async checkUserId() {
    this.spinner.show();
    const queryParams = `?username=${this.userForm.controls.username.value}&count=true`;
    const request = await this.userService.searchUserFull(queryParams);
    this.spinner.hide();
    return request.total;
  }

  delete() {
    Alert.question(Alert.getMessage().question.delete).then((response) => {
      if (response.value) {
        this.subscription.add(
          this.userService.deleteUser(this.queryParams.id).subscribe(
            () =>
              Alert.success(Alert.getMessage().success.delete).then(() =>
                this.router.navigate(["/user/list"])
              ),
            () => Alert.error(Alert.getMessage().errors.delete)
          )
        );
      }
    });
  }

  changePassword() {
    this.router.navigate(["/user/pass"], {
      queryParams: { id: this.queryParams.id },
    });
  }

  checkPassword(group: FormGroup) {
    const password = group.controls.password.value;
    const confirmPassword = group.controls.confirmPassword.value;

    if (password !== confirmPassword) {
      group.controls.confirmPassword.setErrors({
        ...group.controls.confirmPassword.errors,
        confirmPassword: true,
      });
    } else if (
      password === confirmPassword &&
      group.controls.confirmPassword.hasError("confirmPassword")
    ) {
      delete group.controls.confirmPassword.errors.match;
      group.controls.confirmPassword.updateValueAndValidity();
    }
  }

  associateAllCompany() {
    this.companiesAvailable.map((company) => {
      this.companiesAssociate.push(company);
    });
    this.companiesAvailable = [];
  }

  desassociateAllCompany() {
    this.companiesAssociate.map((company) => {
      this.companiesAvailable.push(company);
    });
    this.companiesAssociate = [];
  }

  associateAllRegional() {
    this.regionalsAvailable.map((regional) => {
      this.regionalsAssociate.push(regional);
    });
    this.regionalsAvailable = [];
  }

  desassociateAllRegional() {
    this.regionalsAssociate.map((regional) => {
      this.regionalsAvailable.push(regional);
    });
    this.regionalsAssociate = [];
  }

  associateAllLocals() {
    this.serviceLocalsAvailable.map((serviceLocal) => {
      this.serviceLocalsAssociate.push(serviceLocal);
    });
    this.serviceLocalsAvailable = [];
  }

  desassociateAllLocals() {
    this.serviceLocalsAssociate.map((serviceLocal) => {
      this.serviceLocalsAvailable.push(serviceLocal);
    });
    this.serviceLocalsAssociate = [];
  }

  changeStatus() {
    this.subscription.add(
      this.userService.changeStatus(this.queryParams.id).subscribe(
        (res) => {},
        (err) =>
          Alert.error("Ocorreu algum erro ao atualizar o status do usuário")
      )
    );
  }
}
